import { isDefined } from "@clipboard-health/util-ts";
import { useGetCBHSpecialist } from "@src/appV2/ContactCBHInfo/api/useGetCBHSpecialist";
import { BILLING_REP_EMAIL, CbhSpecialistType } from "@src/appV2/ContactCBHInfo/constants";
import { getCBHSpecialistProfile } from "@src/appV2/ContactCBHInfo/utils/getCBHSpecialistProfile";
import { useCurrentUserId } from "@src/appV2/ExperimentalHomeHealth/lib/useCurrentUserId";
import { useSupportArticles } from "@src/appV2/HelpCenter/api/useSupportArticles";
import { APP_V2_USER_EVENTS, useLogEffect } from "@src/appV2/lib/analytics";
import { useIsMobile } from "@src/appV2/redesign/AppLayout/useResponsive";
import { useEffect, useMemo, useState } from "react";

import { ALL_SUPPORT_ARTICLES_CATEGORY } from "./constants";
import { HelpCenterView } from "./HelpCenterView";
import { getFilteredSupportArticleGroups, getSupportArticleCategories } from "./utils";

export function HelpCenterContainer() {
  const isMobile = useIsMobile();
  const workplaceId = useCurrentUserId() ?? undefined;
  const { data, isError, isLoading, isSuccess } = useSupportArticles();
  const supportArticles = useMemo(() => data?.data ?? [], [data?.data]);
  const categories = useMemo(() => getSupportArticleCategories(supportArticles), [supportArticles]);

  const { data: amData } = useGetCBHSpecialist({
    facilityId: workplaceId,
    type: CbhSpecialistType.ACCOUNT_MANAGER,
  });
  const { data: billingData } = useGetCBHSpecialist({
    facilityId: workplaceId,
    type: CbhSpecialistType.BILLING_SPECIALIST,
  });

  const amProfile = isDefined(amData)
    ? getCBHSpecialistProfile(amData, CbhSpecialistType.ACCOUNT_MANAGER)
    : undefined;
  const amSpecialist =
    isDefined(amProfile) && amProfile.name
      ? {
          name: amProfile.name,
          phone: amProfile.phone,
          email: amProfile.email,
          workTimings: amProfile.workTimings,
        }
      : undefined;

  const billingProfile = isDefined(billingData)
    ? getCBHSpecialistProfile(billingData, CbhSpecialistType.BILLING_SPECIALIST)
    : undefined;
  const billingSpecialist =
    isDefined(billingProfile) && billingProfile.name
      ? {
          name: billingProfile.name,
          phone: billingProfile.phone,
          email: BILLING_REP_EMAIL,
          workTimings: billingProfile.workTimings,
        }
      : undefined;

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(ALL_SUPPORT_ARTICLES_CATEGORY);

  useEffect(() => {
    if (!categories.includes(selectedCategory)) {
      setSelectedCategory(ALL_SUPPORT_ARTICLES_CATEGORY);
    }
  }, [categories, selectedCategory]);

  const articleGroups = useMemo(
    () =>
      getFilteredSupportArticleGroups({
        supportArticles,
        searchQuery,
        selectedCategory: ALL_SUPPORT_ARTICLES_CATEGORY,
      }),
    [supportArticles, searchQuery]
  );

  const categoryArticleGroups = useMemo(
    () => getFilteredSupportArticleGroups({ supportArticles, searchQuery: "", selectedCategory }),
    [supportArticles, selectedCategory]
  );

  useLogEffect(APP_V2_USER_EVENTS.HELP_CENTER_PAGE_VIEWED);

  return (
    <HelpCenterView
      amSpecialist={amSpecialist}
      articleGroups={articleGroups}
      categoryArticleGroups={categoryArticleGroups}
      billingSpecialist={billingSpecialist}
      categories={categories}
      isError={isError}
      isLoading={isLoading}
      isMobile={isMobile}
      isSuccess={isSuccess}
      searchQuery={searchQuery}
      selectedCategory={selectedCategory}
      supportArticles={supportArticles}
      onSearchChange={setSearchQuery}
      onSelectCategory={setSelectedCategory}
    />
  );
}
